<script setup>
/**
 * Whole-day range picker for the QA report pages.
 *
 * Two date inputs and a row of preset buttons asked the reader to operate three
 * controls to answer one question. This is the same control the Reports pages
 * use — one chip that states the current range and opens the presets — and it
 * borrows their option list and translation keys so "Last 7 days" means and
 * reads the same in both places.
 *
 * Dates are plain YYYY-MM-DD strings: the QA figures are day-grained in the
 * account's reporting timezone, so a timestamp here would only invite a
 * boundary that does not match the numbers.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
// Straight to the library rather than through ui/DateRangePicker.vue: that
// wrapper still passes `inline`, which vue-datepicker-next dropped, so the
// panel never rendered. Held open by `open` instead, and left to float from its
// anchor the way the Reports filters do.
import DatePicker from 'vue-datepicker-next';

const props = defineProps({
  start: { type: String, default: '' },
  end: { type: String, default: '' },
  minDate: { type: String, default: '' },
  maxDate: { type: String, default: '' },
  // The day "today" means in the account's reporting timezone. Near midnight
  // the reader's clock is on a different date than the one the numbers are
  // bucketed by, and picking Today then picks the wrong day.
  today: { type: String, default: '' },
  // Off by default. The report pages always describe one span and compare it
  // with the one before, so "no dates" is not a state they can render. The
  // incident queue is the opposite: the whole backlog is how it is normally
  // read, and a window is the exception, so it needs a way back out.
  allowAllTime: { type: Boolean, default: false },
});

const emit = defineEmits(['change']);

const { t } = useI18n();

const DAY_MS = 24 * 60 * 60 * 1000;

const toInput = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Anchored on the reporting day when it is known, so every preset counts back
// from the same date the buckets do.
const anchorDate = () => {
  const parsed = props.today ? new Date(`${props.today}T00:00:00`) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
};

const daysAgo = days => toInput(anchorDate().getTime() - days * DAY_MS);

// startOffset/endOffset mirror REPORT constants; LAST_3_MONTHS stops at 89 days
// back, which is exactly the widest window the KPI endpoint will serve.
const OPTIONS = [
  { id: 'TODAY', startOffset: 0, endOffset: 0 },
  { id: 'YESTERDAY', startOffset: 1, endOffset: 1 },
  { id: 'LAST_7_DAYS', startOffset: 6, endOffset: 0 },
  { id: 'LAST_30_DAYS', startOffset: 29, endOffset: 0 },
  { id: 'LAST_3_MONTHS', startOffset: 89, endOffset: 0 },
];

const CUSTOM_ID = 'CUSTOM_DATE_RANGE';
// Not one of OPTIONS: it has no offsets to count back from, and it is the only
// choice that emits empty strings rather than a pair of days.
const ALL_TIME_ID = 'ALL_TIME';

const isOpen = ref(false);
const isPickingCustom = ref(false);
const rootRef = ref(null);

// vue-datepicker-next works in Date objects; everything else here is a
// YYYY-MM-DD string, so the conversion happens at this boundary only.
const toDate = value => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const customRange = computed(() =>
  [toDate(props.start), toDate(props.end)].filter(Boolean)
);

// Same calendar chrome as the Reports pages: Monday first, month before year.
const datePickerLang = {
  formatLocale: {
    firstDayOfWeek: 1,
    weekdaysMin: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMMM',
  monthBeforeYear: false,
};

// The endpoint serves 90 days at most and cannot know the future, so the
// calendar greys out everything it would refuse rather than letting someone
// pick a range that comes back trimmed.
const disabledDate = date => {
  // End of the reporting day, not of the reader's day.
  const latest = new Date(anchorDate());
  latest.setHours(23, 59, 59, 999);
  if (date > latest) return true;
  const floor = toDate(props.minDate);
  return floor ? date < floor : false;
};

const rangeFor = option => ({
  start: daysAgo(option.startOffset),
  end: daysAgo(option.endOffset),
});

const selectedId = computed(() => {
  if (props.allowAllTime && !props.start && !props.end) return ALL_TIME_ID;
  const match = OPTIONS.find(option => {
    const range = rangeFor(option);
    return range.start === props.start && range.end === props.end;
  });
  return match ? match.id : CUSTOM_ID;
});

const optionLabel = id => t(`REPORT.DATE_RANGE_OPTIONS.${id}`);

const allTimeLabel = computed(() => t('QA_DASHBOARD.DATE_RANGE.ALL_TIME'));

const chipLabel = computed(() => {
  if (selectedId.value === ALL_TIME_ID) return allTimeLabel.value;
  return selectedId.value === CUSTOM_ID
    ? `${props.start || '…'} – ${props.end || '…'}`
    : optionLabel(selectedId.value);
});

const close = () => {
  isOpen.value = false;
  isPickingCustom.value = false;
};

const toggle = () => {
  if (isOpen.value) {
    close();
    return;
  }
  isOpen.value = true;
  isPickingCustom.value = false;
};

const pick = option => {
  emit('change', rangeFor(option));
  close();
};

const pickAllTime = () => {
  emit('change', { start: '', end: '' });
  close();
};

const openCustom = () => {
  isPickingCustom.value = true;
};

const onCustomChange = value => {
  const [from, to] = Array.isArray(value) ? value : [];
  if (!(from instanceof Date) || !(to instanceof Date)) return;

  // A backwards range is a slip, not an intent; swap rather than refuse.
  const [start, end] = from <= to ? [from, to] : [to, from];
  emit('change', { start: toInput(start), end: toInput(end) });
  close();
};

// Outside-click detection has to read the path the event travelled, not where
// its target sits now. Choosing "Custom date range" swaps the preset list for
// the calendar, and Vue applies that before this listener runs — the button the
// reader pressed is already detached, contains() says false, and the menu closed
// itself the instant it was asked to open the calendar. composedPath() is
// captured at dispatch, so it still shows the click came from inside.
const isInsidePath = (path, test) =>
  path.some(node => node instanceof Element && test(node));

const onDocumentClick = event => {
  if (!isOpen.value) return;

  const path =
    typeof event.composedPath === 'function' ? event.composedPath() : [];

  if (rootRef.value && path.includes(rootRef.value)) return;
  // The calendar panel is appended to the body, so it is outside this component
  // and would otherwise count as an outside click.
  if (isInsidePath(path, node => node.closest('.mx-datepicker-main'))) return;
  if (rootRef.value && rootRef.value.contains(event.target)) return;

  close();
};

const onKeydown = event => {
  if (event.key === 'Escape') close();
};

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div ref="rootRef" class="qa-daterange">
    <button
      type="button"
      class="qa-daterange-chip"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      data-qa-daterange-toggle
      @click="toggle"
    >
      <span class="qa-daterange-key">{{
        t('QA_DASHBOARD.AGENT_KPI.DATE')
      }}</span>
      <strong>{{ chipLabel }}</strong>
      <span class="qa-daterange-caret" aria-hidden="true">▾</span>
    </button>

    <div
      v-if="isOpen && !isPickingCustom"
      class="qa-daterange-menu"
      role="listbox"
      @click.stop
    >
      <p class="qa-daterange-menu-title">
        {{ t('QA_DASHBOARD.AGENT_KPI.DATE') }}
      </p>
      <button
        v-if="allowAllTime"
        type="button"
        role="option"
        class="qa-daterange-option"
        :class="{ 'qa-daterange-option--on': selectedId === ALL_TIME_ID }"
        :aria-selected="selectedId === ALL_TIME_ID"
        data-qa-daterange-all
        @click="pickAllTime"
      >
        <span>{{ allTimeLabel }}</span>
        <span v-if="selectedId === ALL_TIME_ID" aria-hidden="true">✓</span>
      </button>

      <button
        v-for="option in OPTIONS"
        :key="option.id"
        type="button"
        role="option"
        class="qa-daterange-option"
        :class="{ 'qa-daterange-option--on': selectedId === option.id }"
        :aria-selected="selectedId === option.id"
        @click="pick(option)"
      >
        <span>{{ optionLabel(option.id) }}</span>
        <span v-if="selectedId === option.id" aria-hidden="true">✓</span>
      </button>

      <button
        type="button"
        role="option"
        class="qa-daterange-option qa-daterange-option--custom"
        :class="{ 'qa-daterange-option--on': selectedId === CUSTOM_ID }"
        :aria-selected="selectedId === CUSTOM_ID"
        data-qa-daterange-custom
        @click="openCustom"
      >
        <span>{{ optionLabel(CUSTOM_ID) }}</span>
        <span aria-hidden="true">›</span>
      </button>
    </div>

    <!-- The panel is kept in this menu and pinned to static, so it never depends
         on the library measuring an anchor or computing a position: two things
         that quietly produced an invisible calendar. The input it normally sits
         beside is hidden by opacity rather than display:none, which would make
         it unmeasurable. -->
    <div
      v-if="isOpen && isPickingCustom"
      class="qa-daterange-menu qa-daterange-menu--calendar"
      @click.stop
    >
      <button
        type="button"
        class="qa-daterange-back"
        @click="isPickingCustom = false"
      >
        ‹ {{ t('QA_DASHBOARD.AGENT_KPI.DATE') }}
      </button>
      <div class="qa-daterange-calendar">
        <DatePicker
          range
          confirm
          open
          type="date"
          :append-to-body="false"
          :clearable="false"
          :editable="false"
          :value="customRange"
          :lang="datePickerLang"
          :disabled-date="disabledDate"
          :confirm-text="t('QA_DASHBOARD.AGENT_KPI.APPLY')"
          @change="onCustomChange"
        />
      </div>
    </div>
  </div>
</template>
